import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { api, ApiError, type Kind, type Status } from "../api";
import { Mascot } from "./Mascot";

export const STATUS_LABEL: Record<Status, string> = {
  open: "Open",
  in_progress: "In progress",
  waiting: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
};
export const CUSTOMER_STATUS_LABEL: Record<Status, string> = { ...STATUS_LABEL, waiting: "Waiting on you" };
export const PRIORITY_LABEL = ["Urgent", "High", "Normal", "Low"];
export const KIND_LABEL: Record<Kind, string> = { ticket: "Ticket", feedback: "In-app", task: "Task", github_issue: "GitHub" };
export const EFFORT_LABEL = ["", "Under an hour", "A few hours", "A day or two", "About a week", "Multi-week"];

export function StatusChip({ status, customer = false }: { status: Status; customer?: boolean }) {
  return <span className={`chip chip-${status}`}>{(customer ? CUSTOMER_STATUS_LABEL : STATUS_LABEL)[status]}</span>;
}

export function PriorityChip({ priority }: { priority: number | null | undefined }) {
  if (priority === null || priority === undefined) return <span className="prio prio-none" title="Not triaged">Triage</span>;
  return (
    <span className={`prio prio-${priority}`} title={`${PRIORITY_LABEL[priority]} priority`}>
      P{priority} {PRIORITY_LABEL[priority]}
    </span>
  );
}

export function KindTag({ kind }: { kind: Kind }) {
  return <span className="kind">{KIND_LABEL[kind]}</span>;
}

export function HealthBadge({ band, score }: { band: "healthy" | "watch" | "at_risk"; score: number }) {
  const label = band === "healthy" ? "Healthy" : band === "watch" ? "Watch" : "At risk";
  const color = band === "healthy" ? "var(--good)" : band === "watch" ? "var(--amber)" : "var(--danger)";
  return (
    <span className={`health health-${band}`}>
      <span className="health-dot" aria-hidden="true" />
      {label}
      <span className="health-bar" aria-hidden="true">
        <span style={{ width: `${score}%`, background: color }} />
      </span>
      <span className="mono small muted">{score}</span>
    </span>
  );
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Fetches on mount and whenever `path` changes; `reload` refetches. */
export function useApi<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setLoading(true);
    api<T>(path)
      .then((d) => live && (setData(d), setError(null)))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : "Couldn't load this."))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <p className="muted" role="status">
      {label}
    </p>
  );
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="notice notice-danger" role="alert">
      {message}
    </p>
  );
}

export function Moment({ mood = "happy", title, children }: { mood?: "happy" | "thinking" | "celebrate"; title: string; children?: ReactNode }) {
  return (
    <div className="moment">
      <Mascot mood={mood} />
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function NotFound() {
  return (
    <div className="page-narrow">
      <Moment mood="thinking" title="We couldn't find that page">
        <p className="muted">The link may be old, or the page may have moved.</p>
        <Link href="/" className="btn">
          Back to the portal
        </Link>
      </Moment>
    </div>
  );
}

/** Renders text with DL-1234 references turned into links. */
export function LinkedText({ text, base }: { text: string; base: string }) {
  const parts = text.split(/(\bDL-\d+\b)/g);
  return (
    <>
      {parts.map((p, i) =>
        /^DL-\d+$/.test(p) ? (
          <Link key={i} href={`${base}/${p.slice(3)}`}>
            {p}
          </Link>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/**
 * Model answers: plain text plus the little Markdown models habitually emit —
 * **bold** becomes <strong>, "#" heading markers are dropped — with DL refs linked.
 */
export function AnswerText({ text, base }: { text: string; base: string }) {
  const clean = text.replace(/^#{1,6}\s+/gm, "");
  return (
    <>
      {clean.split(/(\*\*[^*\n]+\*\*)/g).map((seg, i) =>
        /^\*\*[^*\n]+\*\*$/.test(seg) ? (
          <strong key={i}>
            <LinkedText text={seg.slice(2, -2)} base={base} />
          </strong>
        ) : (
          <LinkedText key={i} text={seg} base={base} />
        ),
      )}
    </>
  );
}
